import { useEffect, useMemo, useRef } from "react";

import { jsxToNode } from "@/components/map-pin";
import GenericPin from "@/components/map-pin/generic-pin";
import MapKitScript from "@/components/mapkit-script";
import { useMapKitLoad } from "@/hooks/use-map-kit-load";
import { cn } from "@/lib/utils";

export interface MapProps {
  className?: string;
  location: [number, number];
}

export default function MapKit({ className, location }: MapProps) {
  const { isMapInitialized, mapRef, mapContainerRef, handleMapKitLoad } =
    useMapKitLoad();
  const markerRef = useRef<mapkit.Annotation>(null);

  const center = useMemo(() => {
    const [lng, lat] = location;
    return { lat, lng };
  }, [location]);

  useEffect(() => {
    if (!(isMapInitialized && mapContainerRef.current) || !!mapRef.current) {
      return;
    }

    // biome-ignore lint/correctness/noUndeclaredVariables: legacy
    mapRef.current = new mapkit.Map(mapContainerRef.current, {
      // biome-ignore lint/correctness/noUndeclaredVariables: legacy
      colorScheme: mapkit.Map.ColorSchemes.Dark,
      isRotationEnabled: false,
      isScrollEnabled: false,
      isZoomEnabled: false,
      // biome-ignore lint/correctness/noUndeclaredVariables: legacy
      mapType: mapkit.Map.MapTypes.MutedStandard,
      // biome-ignore lint/correctness/noUndeclaredVariables: legacy
      showsCompass: mapkit.FeatureVisibility.Hidden,
      showsMapTypeControl: false,
    });

    // biome-ignore lint/correctness/noUndeclaredVariables: legacy
    markerRef.current = new mapkit.Annotation(
      // biome-ignore lint/correctness/noUndeclaredVariables: legacy
      new mapkit.Coordinate(center.lat, center.lng),
      () => jsxToNode(<GenericPin />),
      {
        size: { height: 26, width: 26 },
      }
    );

    mapRef.current.addAnnotation(markerRef.current);
    mapRef.current.setRegionAnimated(
      // biome-ignore lint/correctness/noUndeclaredVariables: legacy
      new mapkit.CoordinateRegion(
        // biome-ignore lint/correctness/noUndeclaredVariables: legacy
        new mapkit.Coordinate(center.lat, center.lng),
        // biome-ignore lint/correctness/noUndeclaredVariables: legacy
        new mapkit.CoordinateSpan(0.001, 0.001)
      )
    );
    // biome-ignore lint/suspicious/noExplicitAny: legacy
    (mapRef.current as any)._allowWheelToZoom = true;
  }, [isMapInitialized, center, mapContainerRef, mapRef]);

  return (
    <>
      <div
        className={cn(
          "relative aspect-343/118 overflow-hidden rounded-b-xl md:mt-5 md:aspect-422/127",
          "[&_canvas]:contrast-140 [&_canvas]:grayscale-100",
          'before:absolute before:inset-0 before:z-1 before:content-[""]',
          className
        )}
        ref={mapContainerRef}
      />
      <MapKitScript onLoad={handleMapKitLoad} />
    </>
  );
}
